import {useRef, useState, lazy, Suspense} from 'react'

import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'

import {ContentfulLogoSuite} from '@github-ui/swp-core/components/contentful/ContentfulLogoSuite'
import {useDomReady} from '../../../../lib/utils/platform'
import {Spacer} from './../../_components/Spacer/Spacer'
import HeroCarousel from '../../_components/HeroCarousel/HeroCarousel'
import HeroCarouselControls from '../../_components/HeroCarousel/HeroCarouselControls/HeroCarouselControls'

import type Assets from './../../webgl-utils/assets'

import type {HomeHeroContent} from '../../content'
import {HeroContent} from './HeroContent'
const IntroHero = lazy(() => import('../../IntroHero'))

type Props = {
  contentfulContent: HomeHeroContent
  assetsRef: React.RefObject<Assets | null>
}

export function HeroSection(props: Props) {
  const {
    contentfulContent: {content: heroContent, carouselItems: heroCarouselItems, logos: logosComponent},
    assetsRef,
  } = props

  const [currentCarouselScreenIndex, setCurrentCarouselScreenIndex] = useState<number>(0)

  const heroRef = useRef<HTMLDivElement | null>(null)
  const isDomReady = useDomReady()
  const setCopyScrollOpacity = (copyScrollOpacity: number) => {
    if (heroRef.current) {
      heroRef.current.style.opacity = `${copyScrollOpacity}` // Set the desired opacity value
    }
  }

  const setCopyScrollScale = (copyScrollScale: number) => {
    if (heroRef.current) {
      heroRef.current.style.transform = `scale(${copyScrollScale})` // Set the desired scale value
    }
  }

  return (
    <section id="hero">
      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <div className="lp-Intro">
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <div className="lp-Intro-gradient" />

        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <Spacer className="lp-IntroTop-spacer" size="0" size768="20px" size1012="50px" />

        {isDomReady && (
          <Suspense fallback={null}>
            <IntroHero
              setCopyScrollOpacity={setCopyScrollOpacity}
              setCopyScrollScale={setCopyScrollScale}
              assetsRef={assetsRef}
            />
          </Suspense>
        )}

        {heroContent ? <HeroContent ref={heroRef} contentfulContent={heroContent} /> : null}

        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <Spacer className="lp-IntroVisuals-spacer" size="64px" size768="600px" size1012="660px" />

        <HeroCarousel
          currentScreenIndex={currentCarouselScreenIndex}
          onAutoPlayTimeout={setCurrentCarouselScreenIndex}
          carouselAriaLabel="GitHub features"
        />
      </div>

      {heroCarouselItems ? (
        <HeroCarouselControls
          items={heroCarouselItems.map(item => ({
            label: item.fields.heading || '',
            value: item.fields.id || '',
            description: item.fields.subheading ? documentToPlainTextString(item.fields.subheading) : '',
          }))}
          currentIndex={currentCarouselScreenIndex}
          onChange={setCurrentCarouselScreenIndex}
          carouselDescriptionId="heroCarouselDescription"
        />
      ) : null}

      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      {logosComponent ? <ContentfulLogoSuite component={logosComponent} className="tmp-pb-7 Logos-logoSuite" /> : null}
    </section>
  )
}
